/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import FormatSymbols from './FormatSymbols';
//@import BaseMoserObject (loadtime) // superclass
//@import FormatSymbols (static) // getListSeparator

/**
 * Util class to build simple V5 expressions.
 * @author  PASCALLacombe
 */
export default class ExpressionUtil extends BaseMoserObject{

	static INDENT = "    ";

	static NEWLINE = "\r\n";

	static NEWLINE_INDENT = "\r\n" + "    ";



	/**
	 * Create 'coalesce' V5 expression, wrapping defaultValue if a String
	 * @param {String} value
	 * @param {String} defaultValue
	 * @return {String}
	 */
	static coalesce(value, defaultValue, expressionLocale) {
		var result = "coalesce ( ";
		result+=(value);
		result+=(FormatSymbols.getListSeparator(expressionLocale));
		result+=(" ");
		result+=(defaultValue);
		result+=(" )");
		return result;
	}

	/**
	 * Create a 'negate' V5 expression
	 * @param {String} value
	 * @return {String}
	 */
	static negate(value) {
		var result = "- ";
		result+=(value);
		return result;
	}

	/**
	 * Create a 'not' V5 expression
	 * @param {String} value
	 * @return {String}
	 */
	static not(value) {
		var result = "not ";
		result+=(value);
		return result;
	}

	/**
	 * Create a 'is null' V5 expressions
	 * @param {String} identifier
	 * @return {String}
	 */
	static isNull(identifier) {
		var result = identifier;
		result+=(" is null");
		return result;
	}

	/**
	 * Create a 'is not null' V5 expressions
	 * @param {String} identifier
	 * @return {String}
	 */
	static isNotNull(identifier) {
		var result = identifier;
		result+=(" is not null");
		return result;
	}

	/**
	 * Create 'when/then' V5 expression
	 * @param {String} condition
	 * @param  value
	 * @return {String}
	 */
	static whenThen(condition, thenValue) {
		var result = ExpressionUtil._whenExp(condition);
		result+=(" ");
		result+=(ExpressionUtil._thenExp(thenValue));
		return result;
	}

	/**
	 * Create 'case/when/then' V5 expression
	 * @param  condition
	 * @param  value
	 * @return {String}
	 */
	static caseWhenThen(conditions, thenValues) {
		var expressions = [];
		for (var i = 0; i < conditions.length; ++i) {
			expressions[i] = ExpressionUtil.whenThen(conditions[i], thenValues[i]);
		}
		return ExpressionUtil.assembleCase(expressions);
	}

	/**
	 * Create a search 'case/when/then' V5 expression
	 * @param  condition
	 * @param  value
	 * @return {String}
	 */
	static searchCase(identifier, conditions, thenValues) {
		var result = "case";
		result+=(" ( ");
		result+=(identifier);
		result+=(" ) ");
		result+=("\r\n");
		for (var i = 0; i < conditions.length; ++i) {
			result+=("    ");
			result+=(ExpressionUtil.whenThen(conditions[i], thenValues[i]));
			result+=("\r\n");
		}
		result+=("end");
		return result;
	}

	/**
	 * Create 'cast' V5 expression
	 * @param {String} value
	 * @param {String} dataType
	 * @param {String} expressionLocale
	 * @return {String}
	 */
	static cast(value, dataType, expressionLocale) {
		var result = "cast ( ";
		result+=(value);
		result+=(FormatSymbols.getListSeparator(expressionLocale));
		result+=(" ");
		result+=(dataType);
		result+=(" )");
		return result;
	}

	/**
	 * Create 'ceil' V5 expression
	 * @param {String} value
	 * @param  dataType
	 * @return {String}
	 */
	static ceil(value) {
		var result = "ceil ( ";
		result+=(value);
		result+=(" )");
		return result;
	}

	/**
	 * Create 'floor' V5 expression
	 * @param {String} value
	 * @param  dataType
	 * @return {String}
	 */
	static floor(value) {
		var result = "floor ( ";
		result+=(value);
		result+=(" )");
		return result;
	}

	/**
	 * Create 'if/then/else' V5 expression, wrapping both values if Strings
	 * @param {String} condition
	 * @param {String} thenValue
	 * @param {String} elseValue
	 * @return {String}
	 */
	static ifThenElse(condition, thenValue, elseValue) {
		var result = ExpressionUtil._ifExp(condition);
		result+=("\r\n");
		result+=(ExpressionUtil._thenExp(thenValue));
		result+=("\r\n");
		result+=(ExpressionUtil.elseExp(elseValue));
		return result;
	}

	/**
	 * Create 'if/then/else' V5 expression, wrapping both values if Strings
	 * @param {String} condition
	 * @param {String} thenValue
	 * @param {String} elseValue
	 * @return {String}
	 */
	static inlineIfThenElse(condition, thenValue, elseValue) {
		var result = ExpressionUtil._ifExp(condition);
		result+=(" ");
		result+=(ExpressionUtil._thenExp(thenValue));
		result+=(" ");
		result+=(ExpressionUtil.elseExp(elseValue));
		return result;
	}

	/**
	 * Create 'else' V5 expression: else ( value )
	 * @param {String} value
	 * @return {String}
	 */
	static elseExp(value) {
		var result = "else ( ";
		result+=(value);
		result+=(" )");
		return result;
	}

	/**
	 * Create 'in' V5 expression: identifier is null or identifier in ( 1, 2 ) or identifier in ( 1, 2 ) or identifier is null or identifier in ( 'abc', 'def' ) or identifier is null
	 * @param {String} expressionLocale  expression locale
	 * @param {String} identifier  - identifier to build expression for
	 * @param {String[]} inValues  - literal values to include in IN
	 * @return {String}
	 */
	static in(identifier, inValues, expressionLocale) {
		return ExpressionUtil._assembleSelection(FormatSymbols.getListSeparator(expressionLocale), identifier, inValues, "in", "or", ExpressionUtil.isNull(identifier));
	}

	/**
	 * @param {String} identifier
	 * @param {String[]} values
	 * @param {String} condition
	 * @param {String} booleanCondition
	 * @return {String}
	 */
	static _assembleSelection(listSeparator, identifier, values, condition, booleanCondition, nullIdentifier) {
		var first = true;
		var hasNullLiteral = false;
		var result = "";
		for (var __i_enFor0 = 0, __len_enFor0 = values.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var inValue = values[__i_enFor0];
			if (ExpressionUtil._isNullLiteral(inValue)) {
				hasNullLiteral = true;
				continue;
			}
			if (first) {
				result+=(identifier);
				result+=(" ");
				result+=(condition);
				result+=(" ( ");
				first = false;
			} else {
				result+=(listSeparator);
				result+=(" ");
			}
			result+=(inValue);
		}
		if (!first) {
			result+=(" )");
		}
		if (hasNullLiteral) {
			if (!first) {
				result+=(" ");
				result+=(booleanCondition);
				result+=(" ");
			}
			result+=(nullIdentifier);
		}
		return result;
	}

	/**
	 * Create 'not in' V5 expression: identifier is not null or identifier not in ( 1, 2 ) or identifier not in ( 1, 2 ) and identifier is not null or identifier not in ( 'abc', 'def' ) and identifier is not null
	 * @param {String} identifier  - identifier to build expression for
	 * @param {String[]} notInValues  - literal values to include in 'not in'
	 * @return {String}
	 */
	static notIn(identifier, notInValues, expressionLocale) {
		return ExpressionUtil._assembleSelection(FormatSymbols.getListSeparator(expressionLocale), identifier, notInValues, "not in", "and", ExpressionUtil.isNotNull(identifier));
	}

	/**
	 * Create a 'when' V5 expression for range values: when ( rangeExpression ) then ( value )
	 * @param {String} identifier  - to create expression for
	 * @param {String} from  - can be null if 'to' is non-null
	 * @param {String} to  -  can be null if 'from' is non-null
	 * @param {String} thenValue  - for: then
	 * @param {boolean} inclusive  - when true the expression will include the 'to' value
	 * @return {String}
	 */
	static whenRangeThen(identifier, from, to, thenValue, inclusive) {
		return ExpressionUtil.whenThen(ExpressionUtil.range(identifier, from, to, inclusive), thenValue);
	}

	/**
	 * Create a range V5 expression: identifier >= from and identifier < to or identifier >= from and identifier <= to or identifier >= from or identifier < to or identifier <= to
	 * @param {String} identifier  - to create expression for
	 * @param {String} from  - can be null if 'to' is non-null
	 * @param {String} to  -  can be null if 'from' is non-null
	 * @param {boolean} inclusive  - when true the expression will include the 'to' value
	 * @return {String}
	 */
	static range(identifier, from, to, inclusive) {
		var result = identifier;
		if (from != null) {
			result+=(" >= ");
			result+=(from);
			if (to != null) {
				result+=(" and ");
				result+=(identifier);
			}
		}
		if (to != null) {
			if (inclusive) {
				result+=(" <= ");
			} else {
				result+=(" < ");
			}
			result+=(to);
		}
		return result;
	}

	/**
	 * Assemble a case/when/then expression, meaning wrap all expressions with: case ... end
	 * @param {String[]} expressions
	 * @return {String}
	 */
	static assembleCase(expressions) {
		var result = "case";
		result+=("\r\n");
		for (var __i_enFor0 = 0, __len_enFor0 = expressions.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var expression = expressions[__i_enFor0];
			result+=("    ");
			result+=(expression);
			result+=("\r\n");
		}
		result+=("end");
		return result;
	}

	/**
	 * Creates minimum for report V5 expression:
	 * @param {String} value
	 * @return {String}
	 */
	static minimumForReport(value) {
		var result = "minimum ( ";
		result+=(value);
		result+=(" for report )");
		return result;
	}

	/**
	 * Creates maximum for report V5 expression:
	 * @param {String} value
	 * @return {String}
	 */
	static maximumForReport(value) {
		var result = "maximum ( ";
		result+=(value);
		result+=(" for report )");
		return result;
	}

	/**
	 * Creates ( aggregate( value for scope) ) V5 expression:
	 * @param {String} value
	 * @param {Array} scope
	 * @param {String} aggregate
	 * @return {String}
	 */
	static minimumForScope(value, scope, aggregate, locale) {
		var listSeparator = FormatSymbols.getListSeparator(locale);
		var result = "minimum ( ";
		result+=(aggregate);
		result+=(" ( ");
		result+=(value);
		result+=(" for ");
		for (var i = 0; i < scope.length; ++i) {
			if (i > 0) {
				result+=(listSeparator);
				result+=(" ");
			}
			result+=(scope[i]);
		}
		result+=(" )");
		result+=(" )");
		return result;
	}

	/**
	 * Creates maximum( aggregate( value for scope) ) V5 expression:
	 * @param {String} value
	 * @param {Array} scope
	 * @param {String} aggregate
	 * @return {String}
	 */
	static maximumForScope(value, scope, aggregate, locale) {
		var listSeparator = FormatSymbols.getListSeparator(locale);
		var result = "maximum ( ";
		result+=(aggregate);
		result+=(" ( ");
		result+=(value);
		result+=(" for ");
		for (var i = 0; i < scope.length; ++i) {
			if (i > 0) {
				result+=(listSeparator);
				result+=(" ");
			}
			result+=(scope[i]);
		}
		result+=(" )");
		result+=(" )");
		return result;
	}

	/**
	 * Replace illegal characters in a String literal
	 * @param {BaseMoserObject} value
	 * @return {String}
	 */
	static escapeStringLiteral(value) {
		return value.toString().replace(new RegExp("\\'","g"), "''");
	}

	/**
	 * Wraps and escapes a literal value if its a String.
	 * @param {BaseMoserObject} value
	 */
	static getLiteral(value) {
		if (value == null) {
			return null;
		} else if ((typeof value === "string" || value instanceof String)) {
			var result = "'";
			result+=(value);
			result+=("'");
			return result;
		}
		return value.toString();
	}

	/**
	 * Add indentation to an expression.
	 * @param {String} exp
	 * @return {String}
	 */
	static nest(exp) {
		var result = "\r\n    ";
		result+=(exp.replace(new RegExp("\r\n","g"), "\r\n    "));
		result+=("\r\n");
		return result;
	}

	/**
	 * Return true if obj represents a NULL value.
	 * @param {BaseMoserObject} obj  - obj to check
	 * @return {boolean}
	 */
	static _isNullLiteral(obj) {
		return obj == null || "__NULL_NAME__" == obj;
	}

	/**
	 * Create 'if' V5 expression: if ( condition )
	 * @param {String} condition
	 * @return {String}
	 */
	static _ifExp(condition) {
		var result = "if ( ";
		result+=(condition);
		result+=(" )");
		return result;
	}

	/**
	 * Create 'when' V5 expression: when ( condition )
	 * @param {String} condition
	 * @return {String}
	 */
	static _whenExp(condition) {
		var result = "when ( ";
		result+=(condition);
		result+=(" )");
		return result;
	}

	/**
	 * Create 'then' V5 expression: then ( value )
	 * @param {String} value
	 * @return {String}
	 */
	static _thenExp(value) {
		var result = "then ( ";
		result+=(value);
		result+=(" )");
		return result;
	}

	/**
	 * Create selection bin as a V5 expressions: when ( identifier in ( inValues ) ) then ( value )
	 * @param {String} identifier  - to create expression for
	 * @param {String[]} inValues  - values to include in IN
	 * @param {String} thenValue  - for: then
	 * @return {String}
	 */
	static whenInThen(identifier, inValues, thenValue, locale) {
		return ExpressionUtil.whenThen(ExpressionUtil.in(identifier, inValues, locale), thenValue);
	}

	/**
	 * This is a util class.
	 */
	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/ExpressionUtil.js